<template>
  <div class="form-group">
    <label for="address">
      {{ $t('order.shippingAddress') }}:
    </label>
    <d-row v-if="!readOnly">
      <d-col cols="12" md="6" lg="8" class="d-flex">
        <validation-provider
          rules="required"
          name="address_id"
          class="w-100"
          v-slot="{ errors }">
          <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
            <v-select
              :options="limitedCustomerAddresses"
              :disabled="!customerId || loadingCustomerAddresses"
              v-model="address_id"
              :searchable="true"
              :reduce="address => address.id"
              :placeholder="$t('order.chooseShippingAddress')"
              label="name"
              ref="customer-addresses-filter"
              @input="verifyTransactionType()"
              :filter="filteredAddresses">
              <template slot="no-options">
                {{ $t('order.noAddressesForThisClient') }}
              </template>
              <template slot="option" slot-scope="option">
                <div class="d-center">
                  <template v-if="option.name">{{ option.name }}</template>
                  <template v-if="option.phone"> {{ $t('ui.phoneShort') }} {{ option.phone }} </template>
                  <template v-if="option.email"> @: {{ option.email }} </template>
                  <template v-if="option.name || option.phone || option.email"> - </template>
                  {{ option.company_name }} - {{ option.street }}
                  <template v-if="option.building">{{ option.building }} / {{ option.locale }}</template>
                  ({{ option.postcode }} {{ option.city }})
                </div>
              </template>
              <template slot="selected-option" slot-scope="option">
                <div class="selected d-center">
                  <template v-if="option.name">{{ option.name }}</template>
                  <template v-if="option.phone"> {{ $t('ui.phoneShort') }} {{ option.phone }} </template>
                  <template v-if="option.email"> @: {{ option.email }} </template>
                  <template v-if="option.name || option.phone || option.email"> - </template>
                  {{ option.company_name }} - {{ option.street }}
                  <template v-if="option.building">{{ option.building }} / {{ option.locale }}</template>
                  ({{ option.postcode }} {{ option.city }})
                </div>
              </template>
            </v-select>
          </div>
          <div class="invalid-feedback">
            {{ errors[0] }}
          </div>
          <small
            v-if="isRemovedAddress"
            class="text-danger pt-2 pb-2">
            {{ $t('order.addressRemoved') }}
          </small>
        </validation-provider>

        <d-button
          v-if="selectedAddress"
          theme="primary"
          outline
          class="ml-2 text-nowrap"
          @click.prevent="$bus.$emit('order-show-address-view-popup')">
          {{ $t('order.showAddress') }}
        </d-button>
      </d-col>

      <d-col cols="12" md="6" lg="4">
        <d-button
          theme="primary"
          @click.prevent="showAddressNewPopup"
          :disabled="!customerId || loadingCustomerAddresses"
          outline
          block-level>
          <i class="material-icons">add</i>
          {{ $t('order.addNewShippingAddress') }}
        </d-button>
      </d-col>
    </d-row>
  </div>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import Vue from 'vue';

export default {
  name: 'order-select-address',
  props: [
    'isHiddenInUi',
    'customerId',
    'partnerCustomerId',
    'readOnly'
  ],
  watch: {
    isHiddenInUi (newValue, oldValue) {
      if (newValue === false && oldValue === true) {
        if (this.isRemovedAddress) {
          this.selectedAddress = null;
          this.address_id = null;
        }
      }
    },
    customerId (newValue, oldValue) {
      if (oldValue) {
        this.address_id = null;
      }

      this.fetchAddresses();
    },
    address_id (newValue) {
      if (!this.isHiddenInUi) {
        this.$bus.$emit('order-update-shipment-address-id');
      }

      if (newValue !== 0 && newValue !== null) {
        if (this.customerAddresses && this.customerAddresses.length) {
          let addressData = this.customerAddresses.filter(address => address.id === newValue);

          if (addressData[0]) {
            this.selectedAddress = JSON.parse(JSON.stringify(addressData[0]));
            this.isRemovedAddress = false;
          } else if (this.isHiddenInUi) {
            this.isRemovedAddress = true;
          } else {
            this.selectedAddress = null;
            this.address_id = null;
          }

          if (!this.isHiddenInUi) {
            this.$bus.$emit('order-update-shipment-cost', false);
          }
        }
      } else {
        this.selectedAddress = null;
      }
    },
    customerAddresses () {
      if (this.customerAddresses && this.customerAddresses.length) {
        let addressData = this.customerAddresses.filter(address => address.id === this.address_id);

        if (addressData[0]) {
          this.selectedAddress = JSON.parse(JSON.stringify(addressData[0]));
        } else if (this.isHiddenInUi) {
          this.isRemovedAddress = true;
        } else {
          this.selectedAddress = null;
          this.address_id = null;
        }

        if (!this.isHiddenInUi) {
          this.$bus.$emit('order-update-shipment-cost', false);
        }
      }
    }
  },
  computed: {
    limitedCustomerAddresses () {
      let options = this.customerAddresses;

      if (this.address_id) {
        return options.filter(option => option.id === this.address_id).concat(options.filter(option => option.id !== this.address_id).slice(0, 100));
      } else if (this.$refs['customer-addresses-filter'] && this.$refs['customer-addresses-filter'].search.length < 1) {
        return options.slice(0, 100);
      }

      return options;
    }
  },
  data () {
    return {
      address_id: null,
      customerAddresses: [],
      loadingCustomerAddresses: false,
      loadCustomerAddressesError: false,
      selectedAddress: null,
      isRemovedAddress: false
    }
  },
  mounted () {
    this.$bus.$on('order-select-address-fetch-data', this.fetchAddresses);
    this.$bus.$on('order-select-address-set-id', this.setAddressID);
    this.fetchAddresses();
  },
  methods: {
    fetchAddresses (addressIdToSet = false) {
      if (!this.customerId) {
        return;
      }

      this.loadingCustomerAddresses = true;
      let queryString = '';

      if (this.partnerCustomerId) {
        queryString = '?partner_customer_id=' + this.partnerCustomerId;
      }

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/customers/addresses/items' + queryString,
        outputKey: 'customerAddresses',
        errorKey: 'loadCustomerAddressesError',
        params: {
          customer_id: this.customerId
        },
        successAction: () => {
          this.loadingCustomerAddresses = false;

          Vue.nextTick(() => {
            if (addressIdToSet) {
              this.address_id = addressIdToSet;
            }
          });
        }
      });
    },
    getAddress () {
      let selectedAddress = this.customerAddresses.filter(address => address.id === this.address_id);

      if (selectedAddress.length) {
        return selectedAddress[0];
      }

      return null;
    },
    showAddressNewPopup () {
      this.$bus.$emit('order-show-add-address-popup');
    },
    setAddressID (newID) {
      this.address_id = newID;
    },
    filterByForAddress (option, label, search) {
      return (option.name + ' - ' + option.phone + ' - ' + option.email + ' - ' + option.company_name + ' - ' + option.street + ' ' + option.building + ' / ' + option.locale + ' (' + option.postcode + ' ' + option.city + ')').toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1;
    },
    filteredAddresses (options, search) {
      return options.filter(option => this.filterByForAddress(option, option.id, search)).slice(0, 100);
    },
    verifyTransactionType () {
      if (this.address_id === null) {
        this.$bus.$emit('order-block-transaction-type-field', false);
        return;
      }

      FormUtils.submitInBackground({
        $http: this.$http,
        method: 'get',
        endpoint: '/post-codes-outside-eu/check-address/' + this.address_id,
        formData: {},
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.postCodeCheckFailed'),
        successAction: (response) => {
          if (typeof response.block_export_transaction_type !== 'undefined') {
            this.$bus.$emit('order-block-transaction-type-field', response.block_export_transaction_type);
          }
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('order-select-address-fetch-data', this.fetchAddresses);
    this.$bus.$off('order-select-address-set-id', this.setAddressID);
  }
}
</script>
